import { useAuth0 } from '@auth0/auth0-react'
import * as coam from '@cimpress-technology/coam-sapidus'
import { LogisticsLocationRoles } from '@cimpress-technology/logistics-configuration-client/js/models/locations'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic'
import { SnackbarController } from '../../common/components/SnackbarController'
import * as locationsStore from '../../common/proxy/locations-store'
import { useLogisticsLocation } from '../../locations/LocationContext'
import { CanonicalCoamUser, locationRoles, getName } from '../models'
import Preloader from '../../common/components/Preloader'
import PermissionsEditor from './PermissionsEditor'

export default function LocationPermissionEditor() {
  const { getAccessTokenSilently, user } = useAuth0()
  const { t } = useTranslation()
  const { logisticsLocation } = useLogisticsLocation()
  const [loading, setLoading] = React.useState(true)
  const [coamGroup, setCoamGroup] = React.useState<
    coam.models.DetailedCoamGroup
  >()
  const fetchData = async () => {
    setLoading(true)
    const coamGroupOverview = await coam.group.findCoamGroupByResourceAndName(
      {
        id: logisticsLocation.id,
        type: coam.models.ResourceTypes.LogisticsLocation,
      },
      `Logistics location (${logisticsLocation.id})`,
      await getAccessTokenSilently(),
      user?.sub ?? ''
    )
    if (!coamGroupOverview) {
      setLoading(false)

      return
    }

    const coamGroupDetail = await coam.group.getCoamGroupDetails(
      coamGroupOverview.id,
      await getAccessTokenSilently(),
      user?.sub ?? '',
      true
    )

    setCoamGroup(coamGroupDetail)
    setLoading(false)
  }

  React.useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logisticsLocation.id])

  if (loading) {
    return <Preloader />
  }

  const removeUser = async (email: string) => {
    const accessToken = await getAccessTokenSilently()
    await locationsStore.removeUser(accessToken, logisticsLocation.id, email)
    await fetchData()
  }

  const saveUser = async (
    coamUser: CanonicalCoamUser,
    role: LogisticsLocationRoles,
    action: 'add' | 'edit'
  ) => {
    const accessToken = await getAccessTokenSilently()
    const email = coamUser.canonical_principal || coamUser.principal
    await locationsStore.addUser(accessToken, logisticsLocation.id, email, role)
    await fetchData()
    const message =
      action === 'add'
        ? t('userManagement.messages.userHasBeenAdded', {
            username: getName(coamUser),
            role,
          })
        : t('userManagement.messages.userHasBeenUpdated', {
            username: getName(coamUser),
            role,
          })

    SnackbarController.show(message, 'success')
  }

  return (
    <>
      <BreadcrumbsItem to={`/location/${logisticsLocation.id}`}>
        {logisticsLocation.name}
      </BreadcrumbsItem>
      <BreadcrumbsItem to="#">
        {t('userManagement.userManagement')}
      </BreadcrumbsItem>
      <PermissionsEditor
        editable={logisticsLocation.editable}
        roles={locationRoles}
        saveUser={saveUser}
        removeUser={removeUser}
        coamGroup={coamGroup}
      />
    </>
  )
}
